import { IconCirclePlus, IconPlus } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, {
    useEffect, useState, useMemo, useCallback,
} from 'react';
import { NSCard, NSSwitch } from 'bricks';
import { formatUSDCurrencyTwoDigits } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds } from 'constants/proForma';
import { ITenant, IUnitMix } from 'views/ProFormaSetup/types';
import { IExpense } from 'views/ProFormaSetup/hooks/useExpenses';
import { useUnitManager } from 'views/ProFormaSetup/hooks/useUnitManager';
import { useOtherIncomeManager } from 'views/ProFormaSetup/hooks/useOtherIncomeManager';
import TenantsOverviewCard from './components/TenantsOverviewCard/TenantsOverviewCard';
import { getOperationsIncomeTotals } from 'views/ProFormaSetup/helpers';
import { getTenantDateRange } from '../helpers';
import UnitMixOverviewCard from './components/UnitMixOverviewCard/UnitMixOverviewCard';
import OperatingInfo from './components/OperatingInfo/OperatingInfo';
import OtherIncomeCard from './components/OtherIncomeCard/OtherIncomeCard';
import Expenses from './components/Expenses/Expenses';
import './Operations.scss';
import { initialOperationsValues } from '../constants';
import { ProFormaQuickStartValues } from '../types';

interface OperationsProps {
    proFormaValues: ProFormaQuickStartValues
    proFormaModelId: number;
    onUpdate: (values: any) => void;
}

const Operations: React.FC<OperationsProps> = ({
    proFormaValues, proFormaModelId, onUpdate,
}) => {
    const [expensesValues, setExpensesValues] = useState(proFormaValues.operations.expensesValues);
    const [operatingInfoValues, setOperatingInfoValues] = useState(proFormaValues.operations.operatingInfoValues);
    const [expensesDetail, setExpensesDetail] = useState<IExpense[]>(proFormaValues.operations.expensesDetail);
    const [showExpenseDetailToggle, setShowExpenseDetailToggle] = useState(true);
    const [showOtherIncomeToggle, setShowOtherIncomeToggle] = useState(true);
    const [unitArray, setUnitArray] = useState<IUnitMix[]>([]);
    const [tenantArray, setTenantArray] = useState<ITenant[]>([]);

    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId);

    const {
        handleDuplicateOrAdd, handleDelete, handleUpdate,
    } = useUnitManager({
        unitArray,
        tenantArray,
        setUnitArray,
        setTenantArray,
        initialUnits: initialOperationsValues.unitArray,
        initialTenants: initialOperationsValues?.tenantArray,
        isApartment,
    });

    const {
        incomeArray,
        handleAddOrDuplicateIncome,
        handleDeleteIncome,
        handleUpdateIncome,
    } = useOtherIncomeManager({
        initialIncomes: initialOperationsValues.incomeArray,
    });

    useEffect(() => {
        onUpdate?.({
            operatingInfoValues,
            unitArray,
            tenantArray,
            incomeArray,
            expensesValues,
            expensesDetail,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operatingInfoValues, unitArray, tenantArray, incomeArray, expensesValues, expensesDetail]);

    const incomeUnitTotals = getOperationsIncomeTotals(isApartment, unitArray || [], tenantArray || []);

    const totalOtherIncome = useMemo(
        () => incomeArray.reduce((sum, unit) => sum + (unit.amountPerYear !== undefined ? unit.amountPerYear : 0), 0), [incomeArray],
    );

    const handleInputChange = useCallback((key: string, value: number | string) => {
        setExpensesValues((prev: any) => ({ ...prev, [key]: value }));
    }, []);

    const handleDetailChange = useCallback((updated: IExpense[]) => setExpensesDetail(updated), []);
    const handleOperatingInfoChange = useCallback((key: any, value: any) => setOperatingInfoValues((prev: any) => ({ ...prev, [key]: value })), []);

    const { operationStartDate, proFormaSaleDate } = getTenantDateRange(
        proFormaValues.basicInformation.startDate, proFormaValues.basicInformation.constructionStart,
        proFormaValues.basicInformation.constructionDuration, proFormaValues.basicInformation.holdPeriod,
    );

    return (
        <div className="Operations p-3">
            <OperatingInfo onChange={handleOperatingInfoChange} values={operatingInfoValues} />

            <div className="ProFormaWizardStepOne__separator" />
            <div className="ProFormaWizardStepOne__title">
                {isApartment ? 'Unit Mix' : 'Tenants'}
            </div>

            <NSCard className="mt-2 p-2">
                <div className="m-1">
                    <RenderIf isTrue={isApartment}>
                        {unitArray.map(unit => (
                            <div key={unit.id}>
                                <UnitMixOverviewCard
                                    values={unit as IUnitMix}
                                    onDuplicate={() => handleDuplicateOrAdd(unit.id)}
                                    onDelete={() => handleDelete(unit.id)}
                                    onUpdate={handleUpdate}
                                />
                            </div>
                        ))}
                    </RenderIf>

                    <RenderIf isTrue={!isApartment}>
                        {tenantArray.map(tenant => (
                            <div key={tenant.id}>
                                <TenantsOverviewCard
                                    values={tenant}
                                    onDuplicate={() => handleDuplicateOrAdd(tenant.id)}
                                    onDelete={() => handleDelete(tenant.id)}
                                    onUpdate={handleUpdate}
                                    count={tenantArray.length}
                                    isSignupFlow
                                    operationStartDate={operationStartDate}
                                    proFormaSaleDate={proFormaSaleDate}
                                />
                            </div>
                        ))}
                    </RenderIf>
                </div>
                <div className="d-flex flex-column px-1">
                    <NSCard
                        onClick={() => handleDuplicateOrAdd()}
                        className={`cursor--pointer text-white align-items-center
                        justify-content-center mb-2 flex-grow-1 
                        ${isApartment
            ? 'ProFormaWizardStepThree__addApartmentCard'
            : 'ProFormaWizardStepThree__addOtherCard'
        }`}
                    >
                        <NSIcon icon={IconCirclePlus} size="lg" className="pb-1" />
                        <div>Add unit type</div>
                    </NSCard>
                </div>
                <NSCard className="NSCard--level-4">
                    <div className="d-flex justify-content-between text-white font-weight-bold p-2">
                        Total annual rental income
                        <div>{formatUSDCurrencyTwoDigits(incomeUnitTotals?.totalRentalIncome)}</div>
                    </div>
                </NSCard>
            </NSCard>

            <NSCard className="mt-2 p-2" title="Other Income">
                <div className="d-flex justify-content-between">
                    <div>
                        <div className="ProFormaWizardStepThree__unitTitle">Other income</div>
                        <div className="ProFormaWizardStepOne__subtitle">Add additional income sources (e.g. RUBS, Parking)</div>
                    </div>
                    <div className="ProFormaWizardStepOne__subtitle align-self-center">
                        <NSSwitch
                            id="calculateToggle"
                            name="calculateToggle"
                            checked={showOtherIncomeToggle}
                            onChange={() => setShowOtherIncomeToggle(prev => !prev)}
                            label=""
                            containerClassName="text-dark"
                        />
                    </div>
                </div>
                <RenderIf isTrue={showOtherIncomeToggle}>
                    <div className="mt-2">
                        {incomeArray.map(income => (
                            <OtherIncomeCard
                                key={income.id}
                                values={income}
                                onUpdate={handleUpdateIncome}
                                onDelete={() => handleDeleteIncome(income.id)}
                            />
                        ))}
                        <NSCard
                            onClick={() => handleAddOrDuplicateIncome()}
                            className="
                            ProFormaWizardStepThree__addOtherIncomeCard
                            cursor--pointer text-white align-items-center justify-content-center mb-2 flex-grow-1"
                        >
                            <div className="py-3">
                                <NSIcon icon={IconPlus} className="pr-2" />
                                Add other income
                            </div>
                        </NSCard>
                    </div>
                    <NSCard className="NSCard--level-4">
                        <div className="d-flex justify-content-between text-white font-weight-bold p-2">
                            Total other income:
                            <div>{formatUSDCurrencyTwoDigits(totalOtherIncome)}</div>
                        </div>
                    </NSCard>
                </RenderIf>
            </NSCard>

            <div className="ProFormaWizardStepOne__separator" />
            <Expenses
                values={expensesValues}
                proFormaModelId={proFormaModelId}
                onChange={handleInputChange}
                onDetailChange={handleDetailChange}
                showExpenseDetail={showExpenseDetailToggle}
                setShowExpenseDetail={setShowExpenseDetailToggle}
                totalIncomeUnits={incomeUnitTotals.totalIncomeUnits}
                totalSFPerUnits={incomeUnitTotals.totalSFPerUnits}
                initialExpenses={expensesDetail}
            />
        </div>
    );
};

export default Operations;
